import { getLanguageCookie, getCurrencyCookie } from "@/utils";

export interface Prefs {
  language: string;
  currency: string | null;
}

export type PrefsEvent = { type: "changed"; prefs: Prefs; previous: Prefs };

const COOKIE_POLL_INTERVAL_MS = 2000;
const DEBOUNCE_MS = 250;
const WATCHED_ATTRS = ["data-language"] as const;

function readPrefs(): Prefs {
  return { language: getLanguageCookie(), currency: getCurrencyCookie() };
}

function tupleKey(p: Prefs): string {
  return `${p.language}::${p.currency ?? ""}`;
}

/**
 * Mirrors `credentials-watcher.ts`'s poll+MutationObserver shape, but for
 * language/currency: unlike userId/registrationDate, prefs always resolve to
 * *some* value (language defaults to "en"), so there's no appeared/removed
 * case — only "changed".
 */
export function startPrefsWatcher(
  onEvent: (e: PrefsEvent) => void
): () => void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return () => {};
  }

  let current = readPrefs();
  let lastKey = tupleKey(current);
  let debounceHandle: ReturnType<typeof setTimeout> | null = null;
  let isActive = true;

  const evaluate = (): void => {
    if (!isActive) return;
    const next = readPrefs();
    const nextKey = tupleKey(next);
    if (nextKey === lastKey) return;

    const previous = current;
    current = next;
    lastKey = nextKey;
    onEvent({ type: "changed", prefs: next, previous });
  };

  const scheduleEvaluate = (): void => {
    if (!isActive) return;
    if (debounceHandle !== null) clearTimeout(debounceHandle);
    debounceHandle = setTimeout(() => {
      debounceHandle = null;
      evaluate();
    }, DEBOUNCE_MS);
  };

  const observer = new MutationObserver(() => {
    scheduleEvaluate();
  });
  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: [...WATCHED_ATTRS],
  });

  const pollHandle = setInterval(scheduleEvaluate, COOKIE_POLL_INTERVAL_MS);

  return () => {
    isActive = false;
    observer.disconnect();
    clearInterval(pollHandle);
    if (debounceHandle !== null) {
      clearTimeout(debounceHandle);
      debounceHandle = null;
    }
  };
}
